import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { playerAPI } from '../services/api';
import { useSEO } from '../hooks/useSEO';
import RegistrationPartnerAndUpsells from '../components/RegistrationPartnerAndUpsells';
import RegistrationPromoCodeField from '../components/RegistrationPromoCodeField';
import { useRegistrationPromoCode } from '../hooks/useRegistrationPromoCode';

const RegisterContainer = styled.div`
  max-width: 980px;
  margin: 2rem auto;
  background: var(--white, #fff);
  border-radius: var(--radius-xl, 16px);
  box-shadow: var(--shadow-xl, 0 8px 24px rgba(0, 0, 0, 0.08));
  overflow: hidden;
`;

const RegisterHeader = styled.div`
  background: linear-gradient(
    135deg,
    var(--brand-red, #d52b1e) 0%,
    var(--primary-blue, #043663) 50%,
    var(--primary-blue-dark, #032a4d) 100%
  );
  color: var(--white, #fff);
  padding: 2rem;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      45deg,
      rgba(213, 43, 30, 0.3) 0%,
      transparent 50%,
      rgba(4, 54, 99, 0.2) 100%
    );
    pointer-events: none;
  }
`;

const Title = styled.h1`
  position: relative;
  z-index: 1;
  font-size: 2.25rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
  @media (max-width: 640px) {
    font-size: 1.65rem;
  }
`;

const Subtitle = styled.p`
  position: relative;
  z-index: 1;
  font-size: 1.1rem;
  opacity: 0.95;
  margin: 0;
`;

const FormContainer = styled.div`
  padding: 2rem;
  @media (max-width: 640px) {
    padding: 1.25rem;
  }
`;

const FormSection = styled.div`
  margin-bottom: 1.5rem;
`;

const SectionTitle = styled.h3`
  color: var(--primary-blue, #043663);
  font-size: 1.5rem;
  margin-bottom: 1rem;
  border-bottom: 2px solid var(--light-grey, #e5e7eb);
  padding-bottom: 0.5rem;
`;

const Msg = styled.div`
  padding: 0.85rem 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  background: ${(p) => (p.$err ? '#fee2e2' : '#eff6ff')};
  color: ${(p) => (p.$err ? '#991b1b' : '#1e3a5f')};
`;

const WizardStepperRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 1.75rem;
  padding: 0;
  position: relative;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
`;

const WizardStepperTrack = styled.div`
  position: absolute;
  top: 17px;
  left: ${(p) => `calc(100% / ${Math.max(2, p.$count || 2) * 2})`};
  right: ${(p) => `calc(100% / ${Math.max(2, p.$count || 2) * 2})`};
  height: 3px;
  background: #e5e7eb;
  border-radius: 999px;
  z-index: 0;
`;

const WizardStepperFill = styled.div`
  position: absolute;
  top: 17px;
  left: ${(p) => `calc(100% / ${Math.max(2, p.$count || 2) * 2})`};
  height: 3px;
  border-radius: 999px;
  background: linear-gradient(90deg, #043663, #0a558c);
  z-index: 0;
  width: ${(p) => {
    const n = Math.max(2, p.$count || 2);
    const pct = Math.min(100, Math.max(0, Number(p.$pct) || 0)) / 100;
    return `calc((100% - (100% / ${n})) * ${pct})`;
  }};
  max-width: ${(p) => `calc(100% - (100% / ${Math.max(2, p.$count || 2)}))`};
  transition: width 0.25s ease;
`;

const WizardStepItem = styled.button`
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 1;
  min-width: 0;
  max-width: 100%;
  border: none;
  background: transparent;
  padding: 0 0.15rem;
  font: inherit;
  cursor: ${(p) => (p.$clickable ? 'pointer' : 'default')};
  &:focus-visible {
    outline: 2px solid #043663;
    outline-offset: 4px;
    border-radius: 8px;
  }
`;

const WizardStepBubble = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8125rem;
  font-weight: 700;
  flex-shrink: 0;
  background: ${(p) => (p.$done || p.$active ? '#043663' : '#e5e7eb')};
  color: ${(p) => (p.$done || p.$active ? '#fff' : '#9ca3af')};
  box-shadow: ${(p) => (p.$active ? '0 4px 12px rgba(4, 54, 99, 0.35)' : 'none')};
`;

const WizardStepLabel = styled.span`
  font-size: 0.65rem;
  font-weight: 600;
  color: ${(p) => (p.$active || p.$done ? '#043663' : '#9ca3af')};
  margin-top: 0.35rem;
  text-align: center;
  line-height: 1.15;
  max-width: 100%;
  padding: 0 0.15rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  overflow-wrap: anywhere;
`;

const WizardNavRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: space-between;
  margin-top: 1.25rem;
`;

const WizardNavButton = styled.button`
  padding: 0.75rem 1.25rem;
  border-radius: 8px;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  border: ${(p) => (p.$primary ? 'none' : '1px solid #cbd5e1')};
  background: ${(p) => (p.$primary ? '#043663' : '#fff')};
  color: ${(p) => (p.$primary ? '#fff' : '#043663')};
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const CompactTotalBar = styled.div`
  margin-top: 1rem;
  padding: 0.85rem 1rem;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1rem;
  align-items: center;
  justify-content: space-between;
`;

const CompactTotalLabel = styled.div`
  font-size: 0.875rem;
  color: #64748b;
`;

const CompactTotalValue = styled.div`
  font-size: 1.15rem;
  font-weight: 800;
  color: #043663;
`;

const CheckoutSummary = styled.div`
  margin: 1rem 0 1.25rem;
  padding: 1.1rem 1.15rem;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #fff;
`;

const CheckoutLine = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  font-size: 0.9rem;
  color: #374151;
  padding: 0.35rem 0;
  border-bottom: 1px solid #f1f5f9;
  &:last-child {
    border-bottom: none;
  }
`;

const CheckoutTotal = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 0.65rem;
  padding-top: 0.65rem;
  border-top: 1px solid #e2e8f0;
  font-weight: 800;
  font-size: 1.05rem;
  color: #0f172a;
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: var(--text-primary, #1f2937);
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid var(--border-color, #d1d5db);
  border-radius: var(--radius-md, 8px);
  font-size: 1rem;
  box-sizing: border-box;
  &:focus {
    outline: none;
    border-color: var(--primary-blue, #043663);
    box-shadow: 0 0 0 3px rgb(30 64 175 / 0.1);
  }
`;

const HelpText = styled.p`
  margin: 0.35rem 0 0;
  font-size: 0.875rem;
`;

const DeclineLink = styled.button`
  display: block;
  width: 100%;
  margin-top: 0.75rem;
  padding: 0.65rem;
  border: none;
  background: transparent;
  color: #64748b;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  text-decoration: underline;
  &:hover {
    color: #043663;
  }
`;

const emptyUpsell = () => ({
  partner: null,
  upsellSelections: [],
  fulfillment: {
    method: 'delivery',
    useDifferentAddress: false,
    firstName: '',
    lastName: '',
    phone: '',
    street: '',
    postalCode: '',
    city: '',
    country: 'CH',
  },
});

function priceOf(catalog, variantId) {
  const meta = catalog?.[variantId];
  if (meta == null) return 0;
  return typeof meta === 'object' ? Number(meta.unitPriceChf) || 0 : Number(meta) || 0;
}

function isMerch(catalog, variantId) {
  const meta = catalog?.[variantId];
  if (!meta || typeof meta !== 'object') return false;
  return !!meta.includesMerchandise;
}

function chargesShipping(catalog, variantId) {
  const meta = catalog?.[variantId];
  if (!meta || typeof meta !== 'object') return false;
  return !!meta.includesMerchandise && !meta.freeShipping;
}

function labelOf(partnerUpsell, variantId) {
  const catalog = partnerUpsell?.catalogByVariantId || {};
  const meta = catalog[variantId];
  if (meta && typeof meta === 'object' && meta.label) return meta.label;
  const byProduct = partnerUpsell?.catalogByProductId || {};
  for (const p of Object.values(byProduct)) {
    const v = (p.variants || []).find((x) => x.id === variantId);
    if (v) {
      const parts = [p.name, v.sizeLabel || v.colorLabel || v.optionLabel].filter(Boolean);
      return parts.join(' · ') || p.name || 'Item';
    }
  }
  return 'Item';
}

const PlayerRegistrationInvoicePage = () => {
  const { t } = useTranslation();
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState('');
  const [partnerUpsell, setPartnerUpsell] = useState(emptyUpsell);
  const [wizardStep, setWizardStep] = useState(2); // 2 = packages, 3 = checkout (aligned with creator)

  useSEO({
    title: t('registrationUpsell.invoiceTitle', 'Complete registration invoice'),
    canonical: `https://helveticpadelseries.ch/player/registration-invoice/${invoiceId}`,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await playerAPI.getRegistrationInvoice(invoiceId);
        if (!cancelled) {
          const inner =
            res?.data && typeof res.data === 'object' && (res.data.invoice || res.data.season)
              ? res.data
              : res;
          const campaignId =
            inner?.campaignId ||
            inner?.season?.campaignId ||
            inner?.invoice?.campaignId ||
            null;
          const payload = { ...inner, campaignId };
          setData(payload);
          const hint = payload?.partnerMirrorHint;
          if (hint) {
            setPartnerUpsell((prev) => ({
              ...prev,
              partnerMirrorHint: hint,
            }));
          }
        }
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.message || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [invoiceId]);

  const summary = useMemo(() => {
    const catalog = partnerUpsell.catalogByVariantId || {};
    const sels = partnerUpsell.upsellSelections || [];
    const lines = sels.map((s) => {
      const qty = Math.max(1, s.quantity || 1);
      const unit = priceOf(catalog, s.variantId);
      return {
        variantId: s.variantId,
        label: labelOf(partnerUpsell, s.variantId),
        quantity: qty,
        amount: Math.round(unit * qty * 100) / 100,
        merch: isMerch(catalog, s.variantId),
      };
    });
    const packageTotal = lines.reduce((s, l) => s + l.amount, 0);
    const hasMerchandise = lines.some((l) => l.merch);
    const needsPaidShipping = sels.some((s) => chargesShipping(catalog, s.variantId));
    const deliveryFee = needsPaidShipping
      ? Number(
          partnerUpsell.effectiveDeliveryFeeChf != null
            ? partnerUpsell.effectiveDeliveryFeeChf
            : partnerUpsell.deliveryFeeChf
        ) || 0
      : 0;
    const total = Math.round((packageTotal + deliveryFee) * 100) / 100;
    const hasPackage = sels.some((s) => {
      const meta = catalog[s.variantId];
      return (
        meta &&
        typeof meta === 'object' &&
        (meta.productKind === 'entry_pass' || meta.productKind === 'pass_upgrade')
      );
    });
    const fulfillmentBlockReason = partnerUpsell.fulfillmentBlockReason || null;
    return {
      lines,
      packageTotal,
      deliveryFee,
      hasMerchandise,
      total,
      hasPackage,
      fulfillmentReady: hasMerchandise ? !!partnerUpsell.fulfillmentReady : true,
      fulfillmentBlockReason: hasMerchandise ? fulfillmentBlockReason : null,
    };
  }, [partnerUpsell]);

  const getPromoValidatePayload = useCallback(
    () => ({
      seasonId: data?.season?.id || null,
      campaignId: data?.campaignId || data?.season?.campaignId || data?.invoice?.campaignId || null,
      tenant_id: data?.invoice?.tenantId || null,
      teamId: data?.team?.id || null,
      cartTotal: summary.total,
    }),
    [data?.season?.id, data?.campaignId, data?.season?.campaignId, data?.invoice?.campaignId, data?.invoice?.tenantId, data?.team?.id, summary.total]
  );

  const {
    promoCodeInput,
    onPromoInputChange,
    promoCodeValid,
    promoCodeMessage,
    promoCodeData,
    promoApplying,
    applyPromoCode,
    appliedPromoCode,
  } = useRegistrationPromoCode(getPromoValidatePayload);

  const checkoutPromoDiscount = useMemo(() => {
    if (!promoCodeValid || !promoCodeData) return 0;
    const total = summary.total;
    const v = Number(promoCodeData.discountValue);
    if (Number.isNaN(v) || v < 0) return 0;
    if (promoCodeData.discountType === 'percentage') {
      return Math.round(total * (Math.min(v, 100) / 100) * 100) / 100;
    }
    return Math.round(Math.min(v, total) * 100) / 100;
  }, [promoCodeValid, promoCodeData, summary.total]);

  const payableTotal = useMemo(
    () => Math.round(Math.max(0, summary.total - checkoutPromoDiscount) * 100) / 100,
    [summary.total, checkoutPromoDiscount]
  );

  const steps = [
    { step: 2, label: t('register.wizardStepPackages', 'Packages') },
    { step: 3, label: t('register.wizardStepCheckout', 'Checkout') },
  ];
  const activeIdx = Math.max(
    0,
    steps.findIndex((s) => s.step === wizardStep)
  );
  const fillPct = steps.length <= 1 ? 0 : (activeIdx / (steps.length - 1)) * 100;

  const goToWizardStep = (step) => {
    if (step < wizardStep) setWizardStep(step);
  };

  const goNext = () => {
    if (wizardStep === 2) {
      if (!summary.hasPackage) {
        setError(
          t(
            'register.selectPackageFirst',
            'Please select a League Pass or package before checkout.'
          )
        );
        return;
      }
      setError('');
      setWizardStep(3);
    }
  };

  const pay = async () => {
    setPaying(true);
    setError('');
    try {
      if (summary.hasMerchandise && !summary.fulfillmentReady) {
        setError(
          summary.fulfillmentBlockReason ||
            t(
              'registrationUpsell.fixBeforeCheckout',
              'Please fix the delivery details above — then you can checkout.'
            )
        );
        setPaying(false);
        return;
      }

      const catalog = partnerUpsell.catalogByVariantId || {};
      let sels = [...(partnerUpsell.upsellSelections || [])];
      const hasEntry = sels.some((s) => {
        const meta = catalog[s.variantId];
        return (
          meta &&
          typeof meta === 'object' &&
          (meta.productKind === 'entry_pass' || meta.productKind === 'pass_upgrade')
        );
      });
      if (!hasEntry && Array.isArray(data?.products)) {
        const entry = data.products.find(
          (p) => p.productKind === 'entry_pass' && (p.variants || []).length
        );
        if (entry) {
          sels = [{ variantId: entry.variants[0].id, quantity: 1 }, ...sels];
        }
      }

      const hasMerchandise = sels.some((s) => isMerch(catalog, s.variantId));
      const f = partnerUpsell.fulfillment || {};
      const fulfillment = hasMerchandise
        ? {
            method: f.method || 'delivery',
            useDifferentAddress: !!f.useDifferentAddress,
            firstName: f.firstName || '',
            lastName: f.lastName || '',
            phone: f.phone || '',
            street: f.street || '',
            postalCode: f.postalCode || '',
            city: f.city || '',
            country: f.country || 'CH',
          }
        : null;

      if (hasMerchandise && fulfillment.method === 'delivery') {
        if (!fulfillment.street?.trim() || !fulfillment.postalCode?.trim() || !fulfillment.city?.trim()) {
          setError(
            t(
              'registrationUpsell.fixAddressFirst',
              'Home delivery needs street, postal code and city. Fill them in below (or choose Pickup) — then you can checkout.'
            )
          );
          setPaying(false);
          return;
        }
      }

      const res = await playerAPI.checkoutRegistrationInvoice(invoiceId, {
        upsellSelections: sels,
        fulfillment,
        promoCode: appliedPromoCode,
      });
      if (res?.data?.alreadyPaid || res?.alreadyPaid) {
        const teamId = res?.data?.teamId || res?.teamId;
        navigate(
          `/player/payment-success?invoice_id=${invoiceId}${teamId ? `&team_id=${teamId}` : ''}`
        );
        return;
      }
      const url = res?.data?.checkoutUrl || res?.checkoutUrl;
      if (url) window.location.href = url;
      else setError('Checkout unavailable');
    } catch (e) {
      const data = e.response?.data;
      setError(
        [data?.message, data?.hint].filter(Boolean).join(' ') ||
          e.message ||
          t(
            'registrationUpsell.checkoutFailed',
            'Checkout failed. Please fix any missing details and try again.'
          )
      );
    } finally {
      setPaying(false);
    }
  };

  const reject = async () => {
    if (!data?.invitationId) {
      navigate('/player/teams?tab=requests');
      return;
    }
    try {
      await playerAPI.rejectRegistrationInvite(data.invitationId);
      navigate('/player/teams?tab=requests');
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    }
  };

  const teamName = data?.team?.team_name || data?.team?.teamName;
  const seasonName = data?.season?.name;

  if (loading) {
    return (
      <RegisterContainer>
        <FormContainer>{t('common.loading', 'Loading…')}</FormContainer>
      </RegisterContainer>
    );
  }

  if (error && !data) {
    return (
      <RegisterContainer>
        <FormContainer>
          <Msg $err>{error}</Msg>
          <Link to="/player/teams">{t('registrationUpsell.backTeams', 'Back to teams')}</Link>
        </FormContainer>
      </RegisterContainer>
    );
  }

  const inv = data?.invoice;
  const skillBlock = data?.skillBlock;
  const canPay = inv && inv.status === 'pending' && !skillBlock;

  return (
    <RegisterContainer>
      <RegisterHeader>
        <Title>
          {t('registrationUpsell.invoiceTitle', 'Complete your registration')}
        </Title>
        <Subtitle>
          {[teamName, seasonName].filter(Boolean).join(' · ') ||
            t('registrationUpsell.partnerJoinSubtitle', 'Join your partner’s team')}
        </Subtitle>
      </RegisterHeader>
      <FormContainer>
        {error && <Msg $err>{error}</Msg>}
        {inv?.status === 'paid' && (
          <Msg>
            {t('registrationUpsell.alreadyPaid', 'This invoice is already paid.')}{' '}
            <Link to="/player/teams">Teams</Link>
          </Msg>
        )}
        {skillBlock && (
          <Msg $err>
            {skillBlock.message}
            {data?.skillHint && (
              <div>
                {t('registrationUpsell.skillHint', 'Allowed skill range for this team: {{min}} – {{max}}', {
                  min: data.skillHint.min,
                  max: data.skillHint.max,
                })}
              </div>
            )}
          </Msg>
        )}

        {canPay && (
          <>
            <WizardStepperRow role="navigation" aria-label="Registration steps">
              <WizardStepperTrack $count={steps.length} aria-hidden />
              <WizardStepperFill $count={steps.length} $pct={fillPct} aria-hidden />
              {steps.map(({ step, label }, i) => {
                const done = wizardStep > step;
                const active = wizardStep === step;
                const clickable = done;
                return (
                  <WizardStepItem
                    key={step}
                    type="button"
                    $clickable={clickable}
                    onClick={() => clickable && goToWizardStep(step)}
                    aria-current={active ? 'step' : undefined}
                  >
                    <WizardStepBubble $active={active} $done={done}>
                      {done ? '✓' : i + 1}
                    </WizardStepBubble>
                    <WizardStepLabel $active={active} $done={done}>
                      {label}
                    </WizardStepLabel>
                  </WizardStepItem>
                );
              })}
            </WizardStepperRow>

            <p style={{ margin: '0 0 1.25rem', color: '#64748b', fontSize: '0.95rem' }}>
              {t(
                'registrationUpsell.partnerJoinHint',
                'You’re joining as a partner — pick your package, then pay to complete registration. Team name is already set by your teammate.'
              )}
            </p>

            <RegistrationPartnerAndUpsells
              hidePartner
              tenantId={inv.tenantId}
              seasonId={data?.season?.id}
              value={partnerUpsell}
              onChange={setPartnerUpsell}
              partnerMirrorHint={data?.partnerMirrorHint}
              sections={wizardStep === 2 ? 'packages' : wizardStep === 3 ? 'fulfillment' : 'none'}
            />

            {wizardStep === 2 && (
              <>
                <CompactTotalBar>
                  <CompactTotalLabel>
                    {t('register.currentSelection', 'Current selection')}
                    {teamName ? ` · ${teamName}` : ''}
                  </CompactTotalLabel>
                  <CompactTotalValue>CHF {summary.packageTotal.toFixed(2)}</CompactTotalValue>
                </CompactTotalBar>
                <WizardNavRow style={{ justifyContent: 'flex-end' }}>
                  <WizardNavButton
                    type="button"
                    $primary
                    onClick={goNext}
                    disabled={!summary.hasPackage}
                  >
                    {t('register.nextCheckout', 'Next: Checkout')}
                  </WizardNavButton>
                </WizardNavRow>
              </>
            )}

            {wizardStep === 3 && (
              <FormSection>
                <SectionTitle>{t('register.promotionalCode')}</SectionTitle>
                <FormGroup>
                  <Label htmlFor="partnerPromoCode">{t('register.promotionalCode')}</Label>
                  <RegistrationPromoCodeField
                    id="partnerPromoCode"
                    value={promoCodeInput}
                    onChange={onPromoInputChange}
                    onApply={applyPromoCode}
                    applying={promoApplying}
                    valid={promoCodeValid}
                    message={promoCodeMessage}
                    disabled={paying}
                  />
                </FormGroup>

                <SectionTitle>{t('register.orderSummary', 'Order summary')}</SectionTitle>
                <CheckoutSummary>
                  {summary.lines.map((line) => (
                    <CheckoutLine key={line.variantId}>
                      <span>
                        {line.label}
                        {line.quantity > 1 ? ` × ${line.quantity}` : ''}
                      </span>
                      <span>CHF {line.amount.toFixed(2)}</span>
                    </CheckoutLine>
                  ))}
                  {summary.deliveryFee > 0 ? (
                    <CheckoutLine>
                      <span>{t('register.deliveryLineLabel', 'Shipping')}</span>
                      <span>CHF {summary.deliveryFee.toFixed(2)}</span>
                    </CheckoutLine>
                  ) : summary.hasMerchandise &&
                    partnerUpsell.fulfillment?.method === 'pickup' ? (
                    <CheckoutLine>
                      <span>{t('registrationUpsell.summaryPickup', 'Pickup')}</span>
                      <span>{t('registrationUpsell.free', 'Free')}</span>
                    </CheckoutLine>
                  ) : null}
                  {promoCodeValid && checkoutPromoDiscount > 0 ? (
                    <CheckoutLine>
                      <span>{t('register.promoDiscount', 'Promo discount')}</span>
                      <span>- CHF {checkoutPromoDiscount.toFixed(2)}</span>
                    </CheckoutLine>
                  ) : null}
                  <CheckoutTotal>
                    <span>{t('registrationUpsell.summaryTotal', 'Total you pay')}</span>
                    <span>CHF {payableTotal.toFixed(2)}</span>
                  </CheckoutTotal>
                </CheckoutSummary>

                {summary.fulfillmentBlockReason ? (
                  <Msg $err>{summary.fulfillmentBlockReason}</Msg>
                ) : null}

                <WizardNavRow>
                  <WizardNavButton type="button" onClick={() => setWizardStep(2)}>
                    {t('register.backToPackages', 'Back to Packages')}
                  </WizardNavButton>
                  <WizardNavButton
                    type="button"
                    $primary
                    disabled={paying || payableTotal <= 0 || !summary.fulfillmentReady}
                    onClick={pay}
                    title={
                      !summary.fulfillmentReady
                        ? summary.fulfillmentBlockReason ||
                          t(
                            'registrationUpsell.fixBeforeCheckout',
                            'Please fix the delivery details above — then you can checkout.'
                          )
                        : undefined
                    }
                  >
                    {paying
                      ? t('registrationUpsell.paying', 'Opening checkout…')
                      : !summary.fulfillmentReady
                        ? t('registrationUpsell.fixThenPay', 'Fix delivery details to continue')
                        : t('registrationUpsell.payPackageTotal', 'Pay CHF {{amount}} & join', {
                            amount: payableTotal.toFixed(2),
                          })}
                  </WizardNavButton>
                </WizardNavRow>

                <DeclineLink type="button" onClick={reject}>
                  {t('registrationUpsell.declineInvite', 'Decline invitation')}
                </DeclineLink>
              </FormSection>
            )}
          </>
        )}
      </FormContainer>
    </RegisterContainer>
  );
};

export default PlayerRegistrationInvoicePage;
